import type { DomainItem } from '@/types';
import i18n from '@/i18n/config';

import React from 'react';
import { Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import { updateDomain } from '@/store';
import { toast } from 'react-toastify';

interface EditDomainModalProps {
  domain: DomainItem;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function EditDomainModal({ domain, isOpen, onOpenChange, onSuccess }: EditDomainModalProps) {
  const [title, setTitle] = React.useState(domain.title);
  const [tokenLimit, setTokenLimit] = React.useState(
    domain.token_limit != null ? String(domain.token_limit) : ''
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setTitle(domain.title);
      setTokenLimit(domain.token_limit != null ? String(domain.token_limit) : '');
    }
  }, [isOpen, domain.id, domain.title, domain.token_limit]);

  const handleSubmit = React.useCallback(async () => {
    if (!title.trim()) return;

    try {
      setIsSubmitting(true);
      const parsed = tokenLimit.trim() ? parseInt(tokenLimit, 10) : null;
      const token_limit =
        parsed != null && !Number.isNaN(parsed) && parsed >= 1 ? parsed : null;
      await updateDomain(domain.id, { title: title.trim(), token_limit });

      toast.success(i18n.t('copy.domain_updated_successfully'));
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      // Error handled by axios interceptor
    } finally {
      setIsSubmitting(false);
    }
  }, [domain.id, title, tokenLimit, onOpenChange, onSuccess]);

  const handleClose = React.useCallback(() => {
    setTitle(domain.title);
    setTokenLimit(domain.token_limit != null ? String(domain.token_limit) : '');
    onOpenChange(false);
  }, [onOpenChange, domain.title, domain.token_limit]);

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={handleClose} size="md">
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Edit Domain</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                <Input
                  label={i18n.t('columns.title')}
                  placeholder={i18n.t('copy.enter_domain_title')}
                  value={title}
                  onValueChange={setTitle}
                  isRequired
                />
                <Input
                  type="number"
                  min={1}
                  label={i18n.t('copy.domain_event_cap')}
                  placeholder={i18n.t('copy.leave_empty_for_unlimited')}
                  value={tokenLimit}
                  onValueChange={setTokenLimit}
                  description="Max events processed for this domain. Leave empty for unlimited."
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose}>{i18n.t('modals.exportConfirm.cancel')}</Button>
              <Button
                color="primary"
                isLoading={isSubmitting}
                onPress={handleSubmit}
                isDisabled={!title.trim()}
              >{i18n.t('common.save')}</Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
